"use strict";
/**
 * AngularJS button component controller.
 *
 * @packageDocumentation
 * @module Button
 */
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
//#region Util Imports
var lodash_es_1 = require("lodash-es");
//#endregion
var base_1 = require("../base");
var type_1 = require("../../helpers/type");
var button_interfaces_1 = require("./button.interfaces");
var IMRButtonController = /** @class */ (function (_super) {
    tslib_1.__extends(IMRButtonController, _super);
    //#endregion
    //#region Ctor
    function IMRButtonController($element, $sce, buttonService) {
        var _this = _super.call(this) || this;
        _this.$element = $element;
        _this.$sce = $sce;
        _this.buttonService = buttonService;
        /**
         * Whether the button is loaded and states are set.
         * Used by ng-show to avoid flashing a button without states.
         */
        _this.isButtonReady = false;
        //#endregion
        //#region Button
        _this.button = null;
        _this.buttonOptions = null;
        _this.tooltipContent = null;
        //#endregion
        //#region State
        _this.stateClasses = type_1.EMPTY_STRING;
        _this.currentStates = [];
        //#endregion
        //#region Tooltip
        _this.tooltipOptions = null;
        _this.tooltip = null;
        return _this;
    }
    Object.defineProperty(IMRButtonController.prototype, "iconPosition", {
        //#region Members
        get: function () {
            var _a, _b;
            return (_b = (_a = this.options) === null || _a === void 0 ? void 0 : _a.iconPosition) !== null && _b !== void 0 ? _b : button_interfaces_1.IMRButtonIconPosition.Left;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(IMRButtonController.prototype, "isSpaceBetween", {
        /**
         * Whether to apply a css class that inserts space between text and icon.
         */
        get: function () {
            var _a, _b;
            return (_b = (_a = this.options) === null || _a === void 0 ? void 0 : _a.spaceBetween) !== null && _b !== void 0 ? _b : false;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(IMRButtonController.prototype, "isIconOnly", {
        get: function () {
            var _a;
            return ((_a = this.options) === null || _a === void 0 ? void 0 : _a.text) == null;
        },
        enumerable: true,
        configurable: true
    });
    //#endregion
    //#region Lifecycle
    IMRButtonController.prototype.initialize = function (options) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.initializeButton()];
                    case 1:
                        _a.sent();
                        return [4 /*yield*/, this.initializeTooltip()];
                    case 2:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    IMRButtonController.prototype.destroy = function () {
        var _a, _b;
        (_a = this.tooltip) === null || _a === void 0 ? void 0 : _a.dispose();
        (_b = this.button) === null || _b === void 0 ? void 0 : _b.dispose();
    };
    //#endregion
    //#region IIMRButton Implementation
    IMRButtonController.prototype.getInnerButton = function () {
        return this.button;
    };
    IMRButtonController.prototype.getElement = function () {
        return this.$element;
    };
    IMRButtonController.prototype.hasState = function (state) {
        if (state != null) {
            return this.stateExists(state);
        }
        return false;
    };
    IMRButtonController.prototype.setState = function (state) {
        if (state != null && !this.stateExists(state)) {
            this.currentStates.push(state);
            this.updateStates();
        }
    };
    IMRButtonController.prototype.setStates = function (states) {
        var _this = this;
        if (this.button != null) {
            this.button.beginUpdate();
            lodash_es_1.forEach(states, function (state) {
                _this.setState(state);
            });
            this.button.endUpdate();
        }
    };
    IMRButtonController.prototype.clearState = function (state) {
        if (state != null && this.stateExists(state)) {
            this.currentStates = this.removeState(this.currentStates, state);
            this.updateStates();
        }
    };
    IMRButtonController.prototype.clearStates = function () {
        this.currentStates = [];
        this.updateStates();
    };
    IMRButtonController.prototype.toggleState = function (state, isSet) {
        var clearState = this.hasState(state) || (isSet != null ? isSet : false);
        clearState
            ? this.clearState(state)
            : this.setState(state);
    };
    IMRButtonController.prototype.initializeButton = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.prepareButtonOptions();
                        return [4 /*yield*/, this.waitForButtonInitialized()];
                    case 1:
                        _a.sent();
                        this.registerEvents();
                        this.initializedStates();
                        this.isButtonReady = true;
                        return [2 /*return*/];
                }
            });
        });
    };
    IMRButtonController.prototype.prepareButtonOptions = function () {
        this.buttonOptions = lodash_es_1.cloneDeep(this.options.innerButtonOptions) || null;
        if (this.buttonOptions == null) {
            this.buttonOptions = {};
        }
        if (this.options.text !== undefined) {
            this.buttonOptions.text = this.options.text;
        }
        if (this.options.hint !== undefined) {
            this.buttonOptions.hint = this.options.hint;
        }
        if (this.options.disabled != null) {
            this.buttonOptions.disabled = this.options.disabled;
        }
        if (this.options.icon != null) {
            var iconTemplate = this.options.icon.getTemplate();
            var iconName = this.options.icon.getDevExtremeIconName();
            if (iconTemplate != null) {
                this.initializeCustomIconFromTemplate(iconTemplate);
            }
            else if (iconName != null) {
                this.initializeDxIcon(iconName);
            }
        }
    };
    IMRButtonController.prototype.initializeCustomIconFromTemplate = function (iconTemplate) {
        var trustedIconTemplate = this.$sce.trustAsHtml(iconTemplate);
        if (this.buttonOptions != null) {
            if (this.buttonOptions.text != null) {
                this.buttonOptions.template = "<div class=\"imr-button-icon\">" + trustedIconTemplate + "</div><div class=\"dx-button-text\">" + this.buttonOptions.text + "</div>";
            }
            else {
                this.buttonOptions.template = "<div class=\"imr-button-icon\">" + trustedIconTemplate + "</div>";
            }
        }
    };
    IMRButtonController.prototype.initializeDxIcon = function (iconName) {
        if (this.buttonOptions != null) {
            this.buttonOptions.icon = iconName;
        }
    };
    IMRButtonController.prototype.waitForButtonInitialized = function () {
        var _a;
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var buttonInitializedArgs;
            return tslib_1.__generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (!(this.buttonOptions != null)) return [3 /*break*/, 2];
                        return [4 /*yield*/, _super.prototype.waitForOnInitialized.call(this, this.buttonOptions)];
                    case 1:
                        buttonInitializedArgs = _b.sent();
                        this.button = (_a = buttonInitializedArgs.component) !== null && _a !== void 0 ? _a : null;
                        _b.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    IMRButtonController.prototype.registerEvents = function () {
        var _this = this;
        if (this.button != null) {
            this.button.on({
                click: function (e) {
                    _this.onButtonClick(e);
                },
            });
            this.button.element().mouseenter(function () {
                _this.onButtonMouseEnter();
            });
            this.button.element().mouseleave(function () {
                _this.onButtonMouseLeave();
            });
        }
    };
    IMRButtonController.prototype.onButtonClick = function (e) {
        _super.prototype.callEvent.call(this, this.options.onClick, {
            devExpressEvent: e,
        });
    };
    IMRButtonController.prototype.onButtonMouseEnter = function () {
        if (this.button != null) {
            if (this.buttonService.isButtonTextOverflowed(this.button)) {
                this.displayTooltip(this.buttonService.getButtonText(this.button));
            }
            else {
                this.hideTooltip();
            }
        }
    };
    IMRButtonController.prototype.onButtonMouseLeave = function () {
        var _a;
        (_a = this.tooltip) === null || _a === void 0 ? void 0 : _a.hide();
    };
    IMRButtonController.prototype.initializedStates = function () {
        if (this.button != null) {
            this.originalOptions = lodash_es_1.cloneDeep(this.button.option());
            if (this.options.states != null) {
                this.setStates(this.options.states);
            }
        }
    };
    IMRButtonController.prototype.removeState = function (states, state) {
        return lodash_es_1.filter(states, function (currentState) {
            return currentState.getName() !== state.getName();
        });
    };
    IMRButtonController.prototype.setDevExpressOptions = function (state) {
        var _a;
        (_a = this.button) === null || _a === void 0 ? void 0 : _a.option(state.getDevExpressOptions());
    };
    IMRButtonController.prototype.setCSSClasses = function (state) {
        var newClasses = state.getCSSClass();
        if (newClasses != null) {
            this.stateClasses = this.stateClasses + " " + newClasses;
        }
    };
    IMRButtonController.prototype.stateExists = function (state) {
        return (lodash_es_1.find(this.currentStates, function (currentState) {
            return currentState.getName() === state.getName();
        }) != null);
    };
    IMRButtonController.prototype.updateStates = function () {
        var _this = this;
        var _a;
        this.stateClasses = type_1.EMPTY_STRING;
        (_a = this.button) === null || _a === void 0 ? void 0 : _a.option(this.originalOptions);
        lodash_es_1.forEach(this.currentStates, function (currentState) {
            _this.setDevExpressOptions(currentState);
            _this.setCSSClasses(currentState);
        });
    };
    IMRButtonController.prototype.displayTooltip = function (text) {
        if (this.tooltip != null && this.button != null) {
            this.tooltipContent = text;
            this.tooltip.show(this.button.element());
        }
    };
    IMRButtonController.prototype.hideTooltip = function () {
        if (this.tooltip != null) {
            this.tooltipContent = null;
            this.tooltip.hide();
        }
    };
    IMRButtonController.prototype.initializeTooltip = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.prepareTooltipOptions();
                        return [4 /*yield*/, this.waitForTooltipInitialized()];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    IMRButtonController.prototype.prepareTooltipOptions = function () {
        this.tooltipOptions = {
            position: this.options.tooltipPosition || 'bottom',
        };
    };
    IMRButtonController.prototype.waitForTooltipInitialized = function () {
        var _a;
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var tooltipInitializedArgs;
            return tslib_1.__generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (!(this.tooltipOptions != null)) return [3 /*break*/, 2];
                        return [4 /*yield*/, _super.prototype.waitForOnInitialized.call(this, this.tooltipOptions)];
                    case 1:
                        tooltipInitializedArgs = _b.sent();
                        this.tooltip = (_a = tooltipInitializedArgs.component) !== null && _a !== void 0 ? _a : null;
                        _b.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    return IMRButtonController;
}(base_1.IMRBaseComponentController));
exports.IMRButtonController = IMRButtonController;
//# sourceMappingURL=button.controller.js.map